/**
 * useNotificationPreferences
 *
 * Fetches and mutates the current user's notification preferences via Central
 * using canonical paths from @levelup/central-notifications.
 *
 * - GET    `${CENTRAL_URL}/api/notifications/preferences`        → list
 * - PATCH  `${CENTRAL_URL}/api/notifications/preferences/:cat`   → toggle channel
 *
 * Bearer-only. No cookies. No proxy.
 *
 * Mutations are OPTIMISTIC: the local CategoryPreference is updated
 * immediately, then reverted if the PATCH fails. Empty preferences
 * are a real "not-configured" state, not an error.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  NOTIFICATION_PREFERENCES_PATH,
  type CategoryPreference,
  type NotificationChannel,
  type PreferencesResponse,
  type UpdateCategoryParams,
  type UpdateCategoryResponse,
} from '@levelup/central-notifications';
import { selectCentralAccessToken } from '../store/slices/centralAuthSlice';
import { CENTRAL_URL } from '../config/centralAuth';
import type { NotificationsFetchState } from '../adapters/settingsAdapter';

interface UseNotificationPreferencesReturn {
  state: NotificationsFetchState;
  /** Toggle a channel for a category. Optimistic; reverts on PATCH failure. */
  updateCategory: (params: UpdateCategoryParams) => Promise<void>;
}

function applyChannelToggle(
  prefs: CategoryPreference[],
  category: string,
  channels: Partial<Record<NotificationChannel, boolean>>,
): CategoryPreference[] {
  return prefs.map((p) => {
    if (p.category !== category) return p;
    const nextChannels = { ...p.channels };
    for (const [ch, enabled] of Object.entries(channels) as [
      NotificationChannel,
      boolean,
    ][]) {
      const existing = nextChannels[ch];
      if (existing) nextChannels[ch] = { ...existing, enabled };
    }
    return { ...p, channels: nextChannels };
  });
}

export function useNotificationPreferences(): UseNotificationPreferencesReturn {
  const token = useSelector(selectCentralAccessToken);
  const [state, setState] = useState<NotificationsFetchState>({ status: 'idle' });

  // Mirror of the latest "ready" preferences for optimistic updates / revert
  const lastReadyRef = useRef<CategoryPreference[] | null>(null);

  useEffect(() => {
    if (!token || !CENTRAL_URL) {
      setState({ status: 'error', message: 'Not authenticated' });
      return;
    }

    let cancelled = false;
    setState({ status: 'loading' });

    fetch(`${CENTRAL_URL}${NOTIFICATION_PREFERENCES_PATH}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Failed to load preferences: ${res.status}`);
        return (await res.json()) as PreferencesResponse;
      })
      .then((body) => {
        if (cancelled) return;
        const prefs = body.preferences ?? [];
        lastReadyRef.current = prefs;
        if (prefs.length === 0) {
          setState({ status: 'not-configured' });
        } else {
          setState({ status: 'ready', preferences: prefs });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Failed to load preferences',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const updateCategory = useCallback(
    async (params: UpdateCategoryParams) => {
      if (!token || !CENTRAL_URL) return;
      const prev = lastReadyRef.current;
      if (!prev) return;

      // Optimistic
      const optimistic = applyChannelToggle(prev, params.category, params.channels);
      lastReadyRef.current = optimistic;
      setState({ status: 'ready', preferences: optimistic });

      try {
        const res = await fetch(
          `${CENTRAL_URL}${NOTIFICATION_PREFERENCES_PATH}/${encodeURIComponent(params.category)}`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ channels: params.channels }),
          },
        );
        if (!res.ok) throw new Error(`Update failed: ${res.status}`);
        const body = (await res.json()) as UpdateCategoryResponse;

        // Reconcile with server response (canonical truth for this category)
        const reconciled = (lastReadyRef.current ?? optimistic).map((p) =>
          p.category === body.preference.category ? body.preference : p,
        );
        lastReadyRef.current = reconciled;
        setState({ status: 'ready', preferences: reconciled });
      } catch (err) {
        // Revert
        lastReadyRef.current = prev;
        setState({ status: 'ready', preferences: prev });
        // Surface the failure as a transient message via state.error path is
        // not modeled in NotificationsFetchState 'ready' — log + rely on the
        // toggle visibly snapping back to the prior position.
        // eslint-disable-next-line no-console
        console.error(
          '[NotificationPreferences] PATCH failed:',
          err instanceof Error ? err.message : err,
        );
      }
    },
    [token],
  );

  return { state, updateCategory };
}
